import React, { createContext, useCallback, useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle, Info } from 'lucide-react';

type ToastVariant = 'error' | 'success' | 'info';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextType {
  showToast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<ToastItem[]>([]);

  const showToast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = Date.now();
    setItems((prev) => [...prev, { id, message, variant }]);
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  }, []);

  const icon = (v: ToastVariant) => {
    if (v === 'error') return <AlertCircle size={18} color="#f87171" />;
    if (v === 'success') return <CheckCircle size={18} color="var(--accent-primary)" />;
    return <Info size={18} color="#38bdf8" />;
  };

  const bg = (v: ToastVariant) => {
    if (v === 'error') return 'rgba(239, 68, 68, 0.12)';
    if (v === 'success') return 'var(--accent-primary-soft)';
    return 'rgba(56, 189, 248, 0.12)';
  };

  const border = (v: ToastVariant) => {
    if (v === 'error') return 'rgba(239, 68, 68, 0.35)';
    if (v === 'success') return 'var(--accent-primary-glow)';
    return 'rgba(56, 189, 248, 0.35)';
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 20000,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          maxWidth: 'min(360px, calc(100vw - 32px))',
          pointerEvents: 'none',
        }}
      >
        <AnimatePresence>
          {items.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 12, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '14px 16px',
                borderRadius: '12px',
                background: bg(t.variant),
                border: `1px solid ${border(t.variant)}`,
                backdropFilter: 'blur(12px)',
                color: '#e2e8f0',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              }}
            >
              {icon(t.variant)}
              <span>{t.message}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};
